import {useLayoutEffect, useRef, useState, type ReactNode} from 'react'
import {toAbsoluteUrl} from '_metronic/helpers'
import {formatPosterDate, formatPrice, formatR, ShareTrade} from './shareMath'

export const POSTER_W = 1200
export const POSTER_H = 675

const INK = '#f1f5f9'
const MUTED = '#8b93a7'
const FAINT = '#5c6578'
const LINE = 'rgba(255,255,255,0.08)'
const WIN = '#22C55E'
const LOSS = '#E84040'
const FONT = 'Inter, Helvetica, sans-serif'
const NUM = 'ui-monospace, SFMono-Regular, Menlo, monospace'

function PosterChrome({
  kicker,
  children,
}: {
  kicker: string
  children: ReactNode
}) {
  return (
    <div
      style={{
        width: POSTER_W,
        height: POSTER_H,
        background: '#0d0e12',
        color: INK,
        fontFamily: FONT,
        position: 'relative',
        overflow: 'hidden',
        boxSizing: 'border-box',
        padding: '52px 64px 40px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
        <img
          src={toAbsoluteUrl('/media/logos/logo-main.png')}
          alt='BottomUP'
          style={{height: 56, width: 'auto', display: 'block'}}
        />
        <div
          style={{
            color: MUTED,
            fontSize: 14,
            fontWeight: 650,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
          }}
        >
          {kicker}
        </div>
      </div>
      {children}
    </div>
  )
}

function resultTone(value: number | null) {
  if (value == null) return INK
  return value >= 0 ? WIN : LOSS
}

function tradeKicker(trade: ShareTrade) {
  if (trade.status === 'stopped') return 'Stopped'
  if (trade.status === 'success') return 'Target hit'
  return 'Closed'
}

function rCaption(trade: ShareTrade) {
  if (trade.rSource === 'book') return 'Realized · futures book'
  if (trade.rSource === 'prices') return 'Realized · entry, stop, close'
  return 'Scaled exit · booked R unavailable'
}

export function TradePoster({trade, traderName}: {trade: ShareTrade; traderName: string}) {
  const tone = resultTone(trade.realizedR)
  const prices = [
    {label: 'Entry', value: trade.entry},
    {label: 'Close', value: trade.close},
    {label: 'Stop', value: trade.stop},
  ].filter((row) => row.value != null)
  const meta = [trade.category, formatPosterDate(trade.closedAt)].filter(Boolean).join('  ·  ')

  return (
    <PosterChrome kicker={tradeKicker(trade)}>
      <div style={{marginTop: 48, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between'}}>
        <div>
          <div style={{fontSize: 64, fontWeight: 680, letterSpacing: '-0.045em', lineHeight: 0.95}}>
            {trade.pair}
          </div>
          <div style={{marginTop: 12, color: MUTED, fontSize: 18, letterSpacing: '0.08em'}}>
            {meta}
          </div>
        </div>
        {trade.position && (
          <div
            style={{
              color: trade.position === 'long' ? WIN : LOSS,
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: '0.18em',
            }}
          >
            {trade.position.toUpperCase()}
          </div>
        )}
      </div>

      <div style={{marginTop: 36}}>
        <div
          style={{
            color: tone,
            fontFamily: NUM,
            fontSize: trade.realizedR == null ? 84 : 120,
            fontWeight: 650,
            letterSpacing: '-0.05em',
            lineHeight: 0.9,
          }}
        >
          {trade.realizedR == null ? 'NO BOOK R' : formatR(trade.realizedR)}
        </div>
        <div
          style={{
            marginTop: 14,
            color: FAINT,
            fontSize: 14,
            fontWeight: 650,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
          }}
        >
          {rCaption(trade)}
        </div>
      </div>

      <div style={{flex: 1}} />

      {(prices.length > 0 || trade.tpHits.length > 0) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 32,
            borderTop: `1px solid ${LINE}`,
            paddingTop: 22,
          }}
        >
          <div style={{display: 'flex', gap: 36}}>
            {prices.map((row) => (
              <div key={row.label}>
                <div
                  style={{
                    color: FAINT,
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                  }}
                >
                  {row.label}
                </div>
                <div style={{marginTop: 6, fontFamily: NUM, fontSize: 26, fontWeight: 600}}>
                  {formatPrice(row.value as number)}
                </div>
              </div>
            ))}
          </div>
          {trade.tpHits.length > 0 && (
            <div style={{textAlign: 'right'}}>
              <div
                style={{
                  color: FAINT,
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                }}
              >
                Take profits
              </div>
              <div style={{marginTop: 8, display: 'flex', gap: 8, justifyContent: 'flex-end'}}>
                {trade.tpHits.map((hit, i) => (
                  <div
                    key={i}
                    style={{
                      minWidth: 54,
                      padding: '6px 0',
                      textAlign: 'center',
                      border: `1px solid ${hit ? 'rgba(34,197,94,0.45)' : LINE}`,
                      color: hit ? WIN : FAINT,
                      fontSize: 14,
                      fontWeight: 700,
                    }}
                  >
                    TP{i + 1}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div
        style={{
          marginTop: 22,
          display: 'flex',
          justifyContent: 'space-between',
          color: MUTED,
          fontSize: 16,
        }}
      >
        <span>
          Tracked by <span style={{color: INK}}>{traderName}</span>
        </span>
        <span>bottomup.app</span>
      </div>
    </PosterChrome>
  )
}

export function PerformancePoster({
  traderName,
  netR,
  winRate,
  wins,
  losses,
  rank,
  totalTraders,
}: {
  traderName: string
  netR: number
  winRate: number
  wins: number
  losses: number
  rank: number | null
  totalTraders: number
}) {
  const trades = wins + losses
  const stats = [
    {label: 'Win rate', value: `${winRate.toFixed(1)}%`},
    {label: 'Trades', value: String(trades)},
    {label: 'W / L', value: `${wins} / ${losses}`},
    {
      label: 'Rank',
      value: rank ? `#${rank}` : '—',
      hint: rank && totalTraders ? `of ${totalTraders}` : '',
    },
  ]

  return (
    <PosterChrome kicker='This month'>
      <div style={{marginTop: 56}}>
        <div style={{color: MUTED, fontSize: 16, letterSpacing: '0.14em', textTransform: 'uppercase'}}>
          Futures performance
        </div>
        <div style={{marginTop: 10, fontSize: 56, fontWeight: 680, letterSpacing: '-0.04em', lineHeight: 1}}>
          {traderName}
        </div>
      </div>
      <div style={{marginTop: 36}}>
        <div
          style={{
            color: resultTone(netR),
            fontFamily: NUM,
            fontSize: 128,
            fontWeight: 650,
            letterSpacing: '-0.05em',
            lineHeight: 0.88,
          }}
        >
          {formatR(netR)}
        </div>
        <div
          style={{
            marginTop: 14,
            color: FAINT,
            fontSize: 14,
            fontWeight: 650,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
          }}
        >
          Net R · same book as Performance
        </div>
      </div>
      <div style={{flex: 1}} />
      <div
        style={{
          display: 'flex',
          gap: 28,
          borderTop: `1px solid ${LINE}`,
          paddingTop: 22,
        }}
      >
        {stats.map((stat) => (
          <div key={stat.label} style={{flex: 1}}>
            <div
              style={{
                color: FAINT,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
              }}
            >
              {stat.label}
            </div>
            <div style={{marginTop: 8, fontFamily: NUM, fontSize: 32, fontWeight: 620, color: INK}}>
              {stat.value}
            </div>
            {stat.hint ? (
              <div style={{marginTop: 4, color: MUTED, fontSize: 13}}>{stat.hint}</div>
            ) : null}
          </div>
        ))}
      </div>
      <div style={{marginTop: 22, color: MUTED, fontSize: 16, textAlign: 'right'}}>bottomup.app</div>
    </PosterChrome>
  )
}

export function PosterFrame({children}: {children: ReactNode}) {
  const frameRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(0.45)

  useLayoutEffect(() => {
    const el = frameRef.current
    if (!el) return
    const apply = () => setScale(el.clientWidth / POSTER_W || 0.45)
    apply()
    const observer = new ResizeObserver(apply)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={frameRef}
      style={{position: 'relative', width: '100%', aspectRatio: '16 / 9', overflow: 'hidden'}}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: POSTER_W,
          height: POSTER_H,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        <div style={{width: POSTER_W, height: POSTER_H}}>{children}</div>
      </div>
    </div>
  )
}
